/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import { Modal, Button } from 'react-bootstrap'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'
import Select from 'react-select'
import Spinner from 'react-bootstrap/Spinner'

interface ItemCreateProps {
	show: boolean;
	handleClose: any;
	salesOrderInfo: any;
	id?: number;
	/** When opening from Sales Invoice Details: current invoice data for immediate allocation table display */
	invoiceDetails?: { id: number; invoiceId: string; invoiceAmount: number; advanceAdjustment?: number; idSalesOrders: number };
}

interface PaymentData {
	paymentType: string;
	idSalesOrders: number;
	idSalesInvoice: number;
	idProjects: number;
	idCompanies: number;
	projectName: string;
	remarks: string;
	paymentDate: string;
	paymentMethod: string;
	paymentAmount: number;
	transactionId: string;
	idBanks: number;
	idBankCash: number;
	idSr: number;
	salesType: string;
	invoiceAllocations?: Array<{ idSalesInvoice: number; idSalesOrders: number; paidAmount: number }>;
}

interface InvoiceRow {
	id: number;
	idSalesOrders: number;
	invoiceId: string;
	invoiceAmount: number;
	alreadyPaidAmount: number;
	remainingAmount: number;
	adjustment: number;
	paidAmount: number;
	salesOrderId: string;
}

interface ProjectAmount {
	invoice: number;
	payment: number;
	shipment: number;
}

const initialPaymentData: PaymentData = {
	paymentType: 'advance',
	idSalesOrders: 0,
	idSalesInvoice: 0,
	idProjects: 0,
	idCompanies: 0,
	idBanks: 0,
	idBankCash: 0,
	idSr: 0,
	paymentAmount: 0,
	projectName: '',
	remarks: '',
	paymentDate: '',
	paymentMethod: 'bank_transfer',
	transactionId: '',
	salesType: '',
}

const initialProjectAmount: ProjectAmount = {
	invoice: 0,
	payment: 0,
	shipment: 0
}

const SalesPaymentCreate: React.FC<ItemCreateProps> = ({ show, handleClose, salesOrderInfo, id, invoiceDetails }) => {

	const [maxPayableAmount, setMaxPayableAmount] = useState(0);
	const [paymentType, setPaymentType] = useState('advance');
	const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
	const [projects, setProjects] = useState([]);
	const [project, setProject] = useState({ label: 'Select Project', value: '' });
	const [banks, setBanks] = useState([]);
	const [bank, setBank] = useState({ label: 'Select Bank', value: '' });
	const [mobileWallets, setMobileWallets] = useState([]);
	const [mobileWallet, setMobileWallet] = useState({ label: 'Select Mobile Wallet', value: '' });
	const [cashBoxes, setCashBoxes] = useState([]);
	const [cashBox, setCashBox] = useState({ label: 'Select Cash Box', value: '' });
	const [accounts, setAccounts] = useState([]);
	const [account, setAccount] = useState({ label: 'Select Account Number', value: '' });
	const [chequeAccounts, setChequeAccounts] = useState([]);
	const [chequeAccount, setChequeAccount] = useState({ label: 'Select Account Number', value: '' });
	const [srs, setSrs] = useState([]);
	const [sr, setSr] = useState({ label: 'Select SR', value: '' });
	const [companies, setCompanies] = useState([]);
	const [company, setCompany] = useState({ label: 'Select Client', value: '' });
	const [paymentData, setPaymentData] = useState<PaymentData>(initialPaymentData);
	const [projectAmount, setProjectAmount] = useState<ProjectAmount>(initialProjectAmount);
	const [selectedInvoiceRows, setSelectedInvoiceRows] = useState<InvoiceRow[]>([]);
	const [invoiceOptionsForSelect, setInvoiceOptionsForSelect] = useState<Array<{ label: string; value: number; [key: string]: any }>>([]);
	const [selectedInvoicesMulti, setSelectedInvoicesMulti] = useState<Array<{ label: string; value: number; [key: string]: any }>>([]);

	useEffect(() => {
		if (!show) return;

		setPaymentData(initialPaymentData);
		setProjectAmount(initialProjectAmount)
		setChequeAccount({ label: 'Select Account Number', value: '' })
		setAccount({ label: 'Select Account Number', value: '' })
		setCashBox({ label: 'Select Cash Box', value: '' })
		setSr({ label: 'Select SR', value: '' })
		setMobileWallet({ label: 'Select Mobile Wallet', value: '' })
		setBank({ label: 'Select Bank', value: '' })
		setProject({ label: 'Select Project', value: '' })
		setPaymentMethod('bank_transfer')
		setPaymentType('advance')
		setMaxPayableAmount(0)
		setSelectedInvoiceRows([])
		setSelectedInvoicesMulti([])
		setInvoiceOptionsForSelect([])

		getProjects()
		getCompanies()

		if (salesOrderInfo && (salesOrderInfo.idSalesOrders !== undefined || salesOrderInfo.id_sales_orders !== undefined)) {
			const idOrders = salesOrderInfo.idSalesOrders ?? salesOrderInfo.id_sales_orders;
			// Support both snake_case and camelCase from different API responses
			const idCompanies = salesOrderInfo.id_companies ?? salesOrderInfo.idCompanies ?? salesOrderInfo.company?.id_companies;
			const idProjects = salesOrderInfo.id_projects ?? salesOrderInfo.idProjects ?? salesOrderInfo.project?.id_projects;
			const companyLabel = salesOrderInfo.companyName ?? salesOrderInfo.company?.company_name ?? 'Client';
			setCompany({ label: companyLabel, value: idCompanies ?? 0 });
			getProjectAmountInfo(idProjects);

			// From Order or Invoice Details: load this order's invoices (only this order when idOrders passed)
			if (idOrders && idCompanies != null && idProjects != null) {
				const preSelectId = salesOrderInfo.idSalesInvoice !== undefined && salesOrderInfo.idSalesInvoice !== 0
					? salesOrderInfo.idSalesInvoice
					: undefined;
				getInvoicesByClient(idProjects, idCompanies, idOrders, preSelectId);
			}

			// From Sales Order Details or Sales Invoice Details: default to 'regular' payment type
			setPaymentType('regular');
			if (salesOrderInfo.idSalesInvoice !== 0 && salesOrderInfo.idSalesInvoice !== undefined) {
				// From Sales Invoice Details: show allocation table immediately with current invoice
				if (invoiceDetails && invoiceDetails.id === salesOrderInfo.idSalesInvoice) {
					const adj = Number(invoiceDetails.advanceAdjustment ?? 0);
					const paid = Math.max(0, (invoiceDetails.invoiceAmount ?? 0) - adj);
					const row: InvoiceRow = {
						id: invoiceDetails.id,
						idSalesOrders: invoiceDetails.idSalesOrders,
						invoiceId: invoiceDetails.invoiceId ?? '',
						invoiceAmount: invoiceDetails.invoiceAmount ?? 0,
						alreadyPaidAmount: 0,
						remainingAmount: paid,
						adjustment: adj,
						paidAmount: paid,
						salesOrderId: invoiceDetails.salesOrderId ?? '',
					};
					const opt = { label: `Invoice # ${row.invoiceId} (ID # ${row.id}) Order # ${row.salesOrderId}`, value: row.id, ...row };
					setSelectedInvoiceRows([row]);
					setSelectedInvoicesMulti([opt]);
					setPaymentData({ ...paymentData, paymentType: 'regular', idCompanies, idSalesOrders: idOrders, idProjects, idSalesInvoice: salesOrderInfo.idSalesInvoice, paymentAmount: paid, invoiceAllocations: [{ idSalesInvoice: row.id, idSalesOrders: row.idSalesOrders, paidAmount: paid }] });
				} else {
					setPaymentData({ ...paymentData, paymentType: 'regular', idCompanies, idSalesOrders: idOrders, idProjects, idSalesInvoice: salesOrderInfo.idSalesInvoice });
				}
			} else {
				// From Sales Order Details: regular payment, user will select invoices
				setPaymentData({ ...paymentData, paymentType: 'regular', idCompanies, idSalesOrders: idOrders, idProjects });
			}
		}

	}, [show, salesOrderInfo, id, invoiceDetails]);

	const [isSubmitDisable, setSubmitDisable] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {

		if (paymentData.idProjects !== 0) {
			getAllSalesRepresentative(paymentData.idProjects)
			getMobileWallets(paymentData.idProjects)
			getCashBoxes(paymentData.idProjects)
			getChequeAccounts(paymentData.idProjects)
			getBanks(paymentData.idProjects)
			setBank({ label: 'Select Bank', value: '' })
			setAccount({ label: 'Select Account Number', value: '' })
			setCashBox({ label: 'Select Cash Box', value: '' })
			setMobileWallet({ label: 'Select Mobiel Wallet', value: '' })
			setChequeAccount({ label: 'Select Account Number', value: '' })
			setSr({ label: 'Select SR', value: '' })
		}


	}, [paymentData.idProjects]);

	const handleChange = (e: any) => {
		if (paymentType === 'regular' && selectedInvoiceRows.length === 0 && e.target.name === 'paymentAmount') {
			if (parseFloat(e.target.value) > maxPayableAmount) {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Max Payable Amount Exceed!',
				});
				return;
			}
		}
		setPaymentData({ ...paymentData, [e.target.name]: e.target.value });
	};

	const handleProjectChange = (project: any) => {
		setProject(project);
		if (paymentType === 'regular') {
			setCompany({ label: 'Select Client', value: '' });
			setSelectedInvoiceRows([]);
			setSelectedInvoicesMulti([]);
			setInvoiceOptionsForSelect([]);
		} else {
			getOrdersByProjects(project.value);
		}
		setPaymentData({ ...paymentData, idProjects: project.value, idCompanies: 0, idSr: 0, idSalesOrders: 0, idSalesInvoice: 0, idBanks: 0, idBankCash: 0, paymentAmount: 0 });
	}

	const getOrdersByProjects = (idProjects: number) => {
		fetch(
			process.env.REACT_APP_API_URL + 'sales-order-by-project/' + idProjects + '?type=' + paymentType,
			getRequestOptions()
		)
			.then((resp) => resp.json())
			.then((resp) => {
				setProjectAmount({ ...projectAmount, invoice: parseFloat(resp.total.invoice), payment: parseFloat(resp.total.payment), shipment: parseFloat(resp.total.shipment) });
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const getAllSalesRepresentative = (idProjects: number) => {

		fetch(
			process.env.REACT_APP_API_URL + 'getAllSr?idProjects=' + idProjects,
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {

				let users = resp.map((item: any) => {
					item.label = item.first_name + ' ' + item.last_name;
					item.value = item.id;
					return item
				})
				setSrs(users);

			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const getProjects = () => {
		fetch(
			process.env.REACT_APP_API_URL + 'lead/projects',
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				let projects = resp.data.map((item: any) => {
					item.label = item.name;
					item.value = item.id;
					return item
				})
				setProjects(projects);
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const getCompanies = () => {
		fetch(
			process.env.REACT_APP_API_URL + 'lead/companies',
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				let companiesObj = resp.data.map((item: any) => {
					item.label = item.name + ' (ID:'+item.id+')';
					item.value = item.id;
					return item
				})
				setCompanies(companiesObj);
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const getMaxPaymentAmount = () => {
		const payload: any = {
			paymentType: paymentType,
			idSalesOrders: paymentData.idSalesOrders || undefined,
			idSalesInvoice: paymentData.idSalesInvoice || undefined,
			idCompanies: paymentData.idCompanies || undefined,
			idProjects: paymentData.idProjects || undefined,
		};
		fetch(
			process.env.REACT_APP_API_URL + 'sales-payment/get-max-payable-amount',
			postRequestOptions(payload)
		)
			.then((resp) => resp.json())
			.then((resp) => {
				setMaxPayableAmount(resp.maxPayableAmount ?? 0);
			})
			.catch(() => setMaxPayableAmount(0));
	}

	const getChequeAccounts = (idProjects: number) => {
		fetch(
			process.env.REACT_APP_API_URL + 'chequeAccounts?idProjects=' + idProjects,
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				let accounts = resp.data.map((item: any) => {
					item.label = item.accountNumber;
					item.value = item.idBankCash;
					return item
				})
				setChequeAccounts(accounts);
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const getBanks = (idProjects: number) => {
		fetch(
			process.env.REACT_APP_API_URL + 'cashBanks?idProjects=' + idProjects,
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				let banks = resp.data.map((item: any) => {
					item.label = item.bankNameShort;
					item.value = item.idBanks;
					return item
				})
				setBanks(banks);
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const getProjectAmountInfo = (idProjects: number) => {

	}

	const getInvoicesByClient = (idProjects: number, idCompanies: number, idSalesOrders?: number, preSelectInvoiceId?: number) => {
		let url = process.env.REACT_APP_API_URL + 'sales-payment/invoices-by-client?idProjects=' + idProjects + '&idCompanies=' + idCompanies;
		if (idSalesOrders != null) {
			url += '&idSalesOrders=' + idSalesOrders;
		}
		fetch(url, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				if (resp.success && resp.data) {
					const rows: InvoiceRow[] = resp.data.map((item: any) => {
						const invAmt = Number(item.invoiceAmount ?? item.invoice_amount ?? 0);
						const adj = Number(item.advanceAdjustment ?? item.advance_adjustment ?? 0);
						const alreadyPaid = Number(item.alreadyPaidAmount ?? item.already_paid_amount ?? 0);
						const amountToPay = Math.max(0, invAmt - adj - alreadyPaid);
						return {
							id: item.id,
							idSalesOrders: item.idSalesOrders ?? item.id_sales_orders ?? 0,
							invoiceId: item.invoiceId ?? item.invoice_id ?? '',
							invoiceAmount: invAmt,
							alreadyPaidAmount: alreadyPaid,
							remainingAmount: Number(item.remainingAmount ?? item.remaining_amount ?? 0),
							adjustment: adj,
							paidAmount: amountToPay,
							salesOrderId: item.salesOrderId ?? '',
						};
					});
					const options = rows.map((r: InvoiceRow) => ({
						label: `Invoice # ${r.invoiceId} (ID # ${r.id}) Order # ${r.salesOrderId}`,
						value: r.id,
						...r,
					}));
					setInvoiceOptionsForSelect(options);
					// From Sales Invoice Details: pre-select single invoice and show allocation table
					if (preSelectInvoiceId != null && rows.some((r) => r.id === preSelectInvoiceId)) {
						const row = rows.find((r) => r.id === preSelectInvoiceId)!;
						const option = options.find((o) => o.id === preSelectInvoiceId)!;
						setSelectedInvoiceRows([row]);
						setSelectedInvoicesMulti([option]);
						setPaymentData((prev) => ({
							...prev,
							paymentAmount: Number(row.paidAmount) || 0,
							invoiceAllocations: [{ idSalesInvoice: row.id, idSalesOrders: row.idSalesOrders, paidAmount: Number(row.paidAmount) || 0 }],
						}));
					}
				} else {
					setInvoiceOptionsForSelect([]);
				}
			})
			.catch(() => {
				setInvoiceOptionsForSelect([]);
			});
	}

	const getMobileWallets = (idProjects: number) => {
		fetch(
			process.env.REACT_APP_API_URL + 'mobileWallets?idProjects=' + idProjects,
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				let wallets = resp.data.map((item: any) => {
					item.label = item.walletName + ' - ' + item.walletNumber;
					item.value = item.idBankCash;
					return item
				})
				setMobileWallets(wallets);
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const getCashBoxes = (idProjects: number) => {
		fetch(
			process.env.REACT_APP_API_URL + 'cashboxes?idProjects=' + idProjects,
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				let boxes = resp.data.map((item: any) => {
					item.label = item.cashBoxName + ' - ' + item.cashBoxLocation;
					item.value = item.idBankCash;
					return item
				})
				setCashBoxes(boxes);
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const salesPaymentSave = async (event: any) => {
		setLoading(true);
		event.preventDefault()
		setSubmitDisable(true);
		const confirm = await Swal.fire({
			title: 'Are you sure?',
			text: "You won't be able to revert this!",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#ec008c',
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Yes, Submit it!',
		})
		if (confirm.value) {

			const postData = {
				salesPayment: paymentData,
			}
			const requestOptions = postRequestOptions(postData)
			try {
				let response = await fetch(
					`${process.env.REACT_APP_API_URL}sales-payment`,
					requestOptions
				)
				let respData = await response.json()
				if (respData.success === true) {

					const successMsg = respData.successMessage
					Swal.fire({
						icon: 'success',
						title: 'Success',
						text: successMsg,
					})
					handleClose(true)

				} else {
					const errorsMessage = validationErrorMsg(respData.errors).join('<br>')
					Swal.fire({
						icon: 'error',
						title: 'Error',
						html: errorsMessage,
					})
				}
				setSubmitDisable(false);
				setLoading(false);

			} catch (error) {
				setLoading(false);
				console.log(error, 'catch the hoop')
				setSubmitDisable(false);
			}
		}
	}

	useEffect(() => {
		getMaxPaymentAmount();
	}, [paymentData.idSalesOrders, paymentData.idSalesInvoice, paymentData.idCompanies, paymentData.idProjects, paymentType]);

	const handleBankAccountChange = (account: any) => {
		setAccount(account);
		setPaymentData({ ...paymentData, idBankCash: account.value });
	}

	const handleCompanyChange = (companyVal: any) => {
		setCompany(companyVal);
		setPaymentData({ ...paymentData, idCompanies: companyVal?.value || 0 });
		if (paymentType === 'regular' && companyVal?.value && project.value) {
			setSelectedInvoiceRows([]);
			setSelectedInvoicesMulti([]);
			getInvoicesByClient(project.value, companyVal.value);
		}
	}
	const handleMultiInvoiceChange = (selected: any) => {
		setSelectedInvoicesMulti(selected || []);
		if (!selected || selected.length === 0) {
			setSelectedInvoiceRows([]);
			setPaymentData((prev) => ({ ...prev, paymentAmount: 0, invoiceAllocations: undefined }));
			return;
		}
		// Build rows from selected options so adjustment (advance_adjustment) is preserved
		const rows: InvoiceRow[] = selected.map((opt: any) => {
			const adj = Number(opt.adjustment ?? opt.advanceAdjustment ?? opt.advance_adjustment ?? 0);
			const invAmount = Number(opt.invoiceAmount ?? opt.invoice_amount ?? 0);
			const alreadyPaid = Number(opt.alreadyPaidAmount ?? opt.already_paid_amount ?? 0);
			const remaining = Number(opt.remainingAmount ?? opt.remaining_amount ?? 0);
			const paidAmount = Math.max(0, invAmount - adj - alreadyPaid);
			return {
				id: opt.id ?? opt.value,
				idSalesOrders: opt.idSalesOrders ?? opt.id_sales_orders ?? 0,
				invoiceId: opt.invoiceId ?? opt.invoice_id ?? '',
				invoiceAmount: invAmount,
				alreadyPaidAmount: alreadyPaid,
				remainingAmount: remaining,
				adjustment: adj,
				paidAmount,
			};
		});
		setSelectedInvoiceRows(rows);
		const sum = rows.reduce((a, r) => a + (Number(r.paidAmount) || 0), 0);
		setPaymentData((prev) => ({
			...prev,
			paymentAmount: Number(sum),
			invoiceAllocations: rows.map((r) => ({ idSalesInvoice: r.id, idSalesOrders: r.idSalesOrders, paidAmount: Number(r.paidAmount) || 0 })),
		}));
	}
	const handleInvoiceRowPaidAmountChange = (invoiceId: number, value: number) => {
		const row = selectedInvoiceRows.find((r) => r.id === invoiceId);
		if (!row) return;
		const maxAllowed = Math.max(0, row.invoiceAmount - (row.adjustment ?? 0) - row.alreadyPaidAmount);
		if (value > maxAllowed) {
			Swal.fire({
				icon: 'error',
				title: 'Invalid Amount',
				text: `Amount to pay cannot exceed remaining amount (max: ${maxAllowed.toFixed(2)})`,
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 3000,
				timerProgressBar: true,
			});
			return;
		}
		const updated = selectedInvoiceRows.map((r) =>
			r.id === invoiceId ? { ...r, paidAmount: Number(value) } : r
		);
		setSelectedInvoiceRows(updated);
		const sum = updated.reduce((a, r) => a + (Number(r.paidAmount) || 0), 0);
		setPaymentData((prev) => ({
			...prev,
			paymentAmount: Number(sum),
			invoiceAllocations: updated.map((r) => ({ idSalesInvoice: r.id, idSalesOrders: r.idSalesOrders, paidAmount: Number(r.paidAmount) || 0 })),
		}));
	}
	const handleChequeAccountChange = (account: any) => {
		setChequeAccount(account);
		setPaymentData({ ...paymentData, idBankCash: account.value });
	}

	const handleSrChange = (sr: any) => {
		setSr(sr);
		setPaymentData({ ...paymentData, idSr: sr.value });
	}

	const handleBankChange = (bank: any) => {
		setBank(bank);
		setPaymentData({ ...paymentData, idBanks: bank.value });
		fetch(
			process.env.REACT_APP_API_URL + 'bankAccounts/' + bank.value + '?idProjects=' + paymentData.idProjects,
			getRequestOptions()
		)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				setAccount({ label: 'Select Account Number', value: '' })
				let accounts = resp.data.map((item: any) => {
					item.label = item.accountNumber;
					item.value = item.idBankCash;
					return item
				})
				setAccounts(accounts);
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}

	const handlePaymentTypeChange = (e: any) => {
		const newType = e.target.value;
		setPaymentType(newType);
		setProject({ label: 'Select Project', value: '' });
		setSr({ label: 'Select SR', value: '' });
		setCompany({ label: 'Select Client', value: '' });
		setSelectedInvoiceRows([]);
		setSelectedInvoicesMulti([]);
		setInvoiceOptionsForSelect([]);
		setPaymentData({ ...paymentData, [e.target.name]: newType, idProjects: 0, idCompanies: 0, idSalesOrders: 0, idSalesInvoice: 0, paymentAmount: 0, invoiceAllocations: undefined });
	};

	const handleMobileWalletChange = (mobileWallet: any) => {
		setMobileWallet(mobileWallet);
		setPaymentData({ ...paymentData, idBankCash: mobileWallet.value });
	};

	const handleCashboxChange = (cashBox: any) => {
		setCashBox(cashBox);
		setPaymentData({ ...paymentData, idBankCash: cashBox.value });
	};

	const handleSelectOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setPaymentData({ ...paymentData, [event.target.id]: event.target.value });
		setPaymentMethod(event.target.value)
	};


	return (
		<Modal show={show} onHide={() => handleClose(false)} size="xl">
			<form onSubmit={salesPaymentSave}>
				<Modal.Header closeButton>
					{paymentData.idSalesOrders !== 0 &&
						<Modal.Title>Sales Order Payment (Order No # {paymentData.idSalesOrders})</Modal.Title>
					}
					{paymentData.idSalesOrders === 0 &&
						<Modal.Title>Create New Sales Payment</Modal.Title>
					}
				</Modal.Header>
				<Modal.Body>
					<div className="form-group row">
						<label htmlFor="paymentType" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Payment Type <span className="required text-danger"></span></label>
						<div className="col-lg-5">
							{salesOrderInfo === null &&
								<>
									<label>

										<input
											name="paymentType"
											type="radio"
											value="advance"
											checked={paymentType === 'advance'}
											onChange={handlePaymentTypeChange}
										/>
										&nbsp;Advance/Bulk Payment
									</label>
									&nbsp;&nbsp;&nbsp;&nbsp;
								</>
							}
							<label>
								<input
									name="paymentType"
									type="radio"
									value="regular"
									checked={paymentType === 'regular'}
									onChange={handlePaymentTypeChange}
								/>
								&nbsp;Regular
							</label>

						</div>
					</div>
					<div className="form-group row">
						<label htmlFor="project" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select Project <span className="required text-danger"></span></label>
						<div className="col-lg-5">

							{salesOrderInfo === null &&
								<Select
									value={project}
									onChange={handleProjectChange}
									options={projects}
									menuPosition="fixed"
								/>
							}
							{(salesOrderInfo !== null) &&
								<span>{salesOrderInfo.projectName}</span>
							}


						</div>
					</div>

					{paymentType === 'regular' && (
						<>
							<div className="form-group row">
								<label htmlFor="client" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select Client <span className="required text-danger"></span></label>
								<div className="col-lg-5">
									{salesOrderInfo === null && (
										<Select
											value={company}
											onChange={handleCompanyChange}
											options={companies}
											menuPosition="fixed"
											isClearable={true}
										 placeholder="Select Client"
										/>
									)}
									{salesOrderInfo !== null && (
										<input
											type="text"
											className="form-control form-control-sm bg-light border"
											value={salesOrderInfo.companyName ?? salesOrderInfo.company?.company_name ?? salesOrderInfo.projectName ?? ''}
											readOnly
											style={{ fontWeight: 500, color: '#1e3a5f' }}
										/>
									)}
								</div>
							</div>
							{/* From Sales Order Details: show dropdown. From Sales Invoice Details: hide dropdown, only show Invoice allocation table */}
							{!(salesOrderInfo !== null && salesOrderInfo.idSalesInvoice !== undefined && salesOrderInfo.idSalesInvoice !== 0) && (
								<div className="form-group row">
									<label className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select Sales Invoice(s) <span className="required text-danger"></span></label>
									<div className="col-lg-5">
										<Select
											value={selectedInvoicesMulti}
											onChange={handleMultiInvoiceChange}
											options={invoiceOptionsForSelect}
											menuPosition="fixed"
											isMulti
											placeholder="Select one or more invoices"
											className="sales-invoice-select"
										/>
										{invoiceOptionsForSelect.length === 0 && salesOrderInfo !== null && (
											<small className="text-muted">No unpaid invoices found for this order</small>
										)}
									</div>
								</div>
							)}
							{selectedInvoiceRows.length > 0 && (
								<div className="form-group row">
									<label className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Invoice Allocation</label>
									<div className="col-lg-9">
										<div className="card border shadow-sm">
											<div className="card-body p-0">
												<div className="table-responsive">
													<table className="table table-bordered table-hover table-sm mb-0">
														<thead className="table-light">
															<tr>
																<th className="text-nowrap">Invoice NO</th>
																<th className="text-end">Invoice Amount</th>
																<th className="text-end">Adjusted Amount</th>
																<th className="text-end">Already Paid</th>
																<th className="text-end">Amount to Pay</th>
															</tr>
														</thead>
														<tbody>
															{selectedInvoiceRows.map((row) => (
																<tr key={row.id}>
																	<td className="fw-semibold">{row.invoiceId}</td>
																	<td className="text-end">{Number(row.invoiceAmount).toFixed(2)}</td>
																	<td className="text-end">{Number(row.adjustment ?? 0).toFixed(2)}</td>
																	<td className="text-end">{Number(row.alreadyPaidAmount).toFixed(2)}</td>
																	<td>
																		<input
																			type="number"
																			step="any"
																			className="form-control form-control-sm text-end"
																			value={Number(row.paidAmount) || ''}
																			onChange={(e) => handleInvoiceRowPaidAmountChange(row.id, Number(e.target.value) || 0)}
																		/>
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
						</>
					)}

					{paymentType === 'advance' && (
						<>
							<div className="form-group row">
								<label className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select Client <span className="required text-danger"></span></label>
								<div className="col-lg-5">
									{salesOrderInfo === null && (
										<Select
											value={company}
											onChange={handleCompanyChange}
											options={companies}
											menuPosition="fixed"
											isClearable={true}
											placeholder="Select Client"
										/>
									)}
									{salesOrderInfo !== null && <span>{salesOrderInfo.companyName ?? salesOrderInfo.company?.company_name ?? salesOrderInfo.projectName ?? ''}</span>}
								</div>
							</div>
							<div className="form-group row">
								<label className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select SR</label>
								<div className="col-lg-5">
									<Select
										value={sr}
										onChange={handleSrChange}
										options={srs}
										menuPosition="fixed"
										isClearable={true}
									/>
								</div>
							</div>
							<div className="form-group row">
								<label className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Sales Type</label>
								<div className="col-lg-5">
									<label>
										<input name="salesType" type="radio" value="Inter Business Unit" onChange={handleChange} />
										&nbsp;Inter Business Unit
									</label>
									&nbsp;&nbsp;&nbsp;&nbsp;
									<label>
										<input name="salesType" type="radio" value="Corporate" onChange={handleChange} />
										&nbsp;Corporate
									</label>
								</div>
							</div>
						</>
					)}
					<div className="form-group row">
						<label htmlFor="idCompanyProjects" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Payment Method <span className="required text-danger"></span></label>
						<div className="col-lg-5">
							<select name='paymentMethod' id="paymentMethod" onChange={handleSelectOnChange} className="form-control form-control-sm">
								<option value={'bank_transfer'}>Bank Transfer</option>
								<option value={'cash'}>Cash</option>
								<option value={'cheque'}>Cheque</option>
								<option value={'mobile_wallet'}>Mobile Wallet</option>
							</select>
						</div>
					</div>
					{paymentMethod === 'bank_transfer' &&
						<>
							<div className="form-group row">
								<label htmlFor="idBanks" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Deposit To Bank <span className="required text-danger"></span></label>
								<div className="col-lg-5">
									<Select
										value={bank}
										onChange={handleBankChange}
										options={banks}
										menuPosition="fixed"
									/>
								</div>
							</div>
							<div className="form-group row">
								<label htmlFor="idBanks" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Deposit Account Number <span className="required text-danger"></span></label>
								<div className="col-lg-5">
									<Select
										value={account}
										onChange={handleBankAccountChange}
										options={accounts}
										menuPosition="fixed"
									/>
								</div>
							</div>
						</>
					}

					{paymentMethod === 'cheque' &&
						<div className="form-group row">
							<label htmlFor="idBanks" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select Account Number <span className="required text-danger"></span></label>
							<div className="col-lg-5">
								<Select
									value={chequeAccount}
									onChange={handleChequeAccountChange}
									options={chequeAccounts}
									menuPosition="fixed"
								/>
							</div>
						</div>
					}

					{paymentMethod === 'mobile_wallet' &&
						<div className="form-group row">
							<label htmlFor="idMobileWallet" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select Mobile Wallet <span className="required text-danger"></span></label>
							<div className="col-lg-5">
								<Select
									value={mobileWallet}
									onChange={handleMobileWalletChange}
									options={mobileWallets}
									menuPosition="fixed"
								/>
							</div>
						</div>
					}

					{paymentMethod === 'cash' &&
						<div className="form-group row">
							<label htmlFor="idCashBoxes" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Select Cash Box <span className="required text-danger"></span></label>
							<div className="col-lg-5">
								<Select
									value={cashBox}
									onChange={handleCashboxChange}
									options={cashBoxes}
									menuPosition="fixed"
								/>
							</div>
						</div>
					}

					<div className="form-group row">
						<label htmlFor="paymentDate" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Payment Date <span className="required text-danger"></span></label>
						<div className="col-lg-5">
							<input type="date" name="paymentDate" id="paymentDate" value={paymentData.paymentDate} onChange={handleChange} className="form-control form-control-sm" />
						</div>
					</div>
					<div className="form-group row">
						<label htmlFor="paymentAmount" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Payment Amount <span className="required text-danger"></span></label>
						<div className="col-lg-5">
							{paymentType === 'regular' && selectedInvoiceRows.length > 0 ? (
								<>
									<input
										type="number"
										name="paymentAmount"
										step="any"
										id="paymentAmount"
										value={paymentData.paymentAmount}
										readOnly
										className="form-control form-control-sm bg-light border fw-semibold"
										style={{ color: '#1e3a5f', cursor: 'default' }}
									/>
									<small className="text-muted">Auto-calculated from Invoice Allocation above</small>
								</>
							) : (
								<>
									<input type="number" name="paymentAmount" step="any" id="paymentAmount" value={paymentData.paymentAmount || ''} onChange={handleChange} className="form-control form-control-sm" />
								</>
							)}
						</div>
					</div>

					{paymentMethod !== 'cash' &&
						<div className="form-group row">
							<label htmlFor="transactionId" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Transaction ID <span className="required text-danger"></span></label>
							<div className="col-lg-5">
								<input type="text" name="transactionId" id="transactionId" value={paymentData.transactionId} onChange={handleChange} className="form-control form-control-sm" />
							</div>
						</div>
					}

					<div className="form-group row">
						<label htmlFor="remarks" className="col-lg-3 col-form-label" style={{ "textAlign": "right" }}>Remarks</label>
						<div className="col-lg-5">
							<textarea rows={4} name="remarks" id="remarks" onChange={handleChange} className="form-control form-control-sm"></textarea>
						</div>
					</div>


					{/* <pre>{JSON.stringify(paymentData, null, 2)}</pre>
					<pre>{JSON.stringify(salesOrderInfo, null, 2)}</pre> */}
				</Modal.Body>
				<Modal.Footer>

					<button type="submit" disabled={isSubmitDisable} className="btn btn-sm btn-primary me-2">
						{loading &&
							<Spinner
								as="span"
								animation="grow"
								size="sm"
								role="status"
								aria-hidden="true"
							/>
						}
						<span>  Submit</span>
					</button>
					<Button variant='secondary' size="sm" onClick={() => handleClose(false)}>
						Close
					</Button>
				</Modal.Footer>
			</form>
		</Modal >
	)
}

export default SalesPaymentCreate